/**
 * Bureaux personnels — règles partagées API / jeu (sous-chemin
 * `@campus/core/personal-desks`, jamais le barrel côté serveur : piège Phaser).
 *
 * Un admin trace un « espace bureaux » (`map_zones.type = 'desk-area'`) ; chaque
 * membre y pose UN bureau (rectangle de la taille fixée par l'espace) puis
 * l'aménage avec les objets autorisés par l'admin (`desk_catalogs`).
 *
 * Coordonnées en cases :
 * - bureau (`x`, `y`) : coin haut-gauche, RELATIF au coin de l'espace (l'admin
 *   déplace l'espace → les bureaux suivent) ;
 * - objet (`dx`, `dy`) : case bas-gauche, RELATIVE au coin haut-gauche du
 *   bureau — même ancrage que `map_furniture.tile_x/tile_y` (objet dessiné
 *   vers le haut à partir de sa rangée du bas).
 */
import { furnitureCatalog, findFurniture, isValidCollisionGrid, baseOnlyCollisionGrid, type CollisionGrid, type FurnitureCatalogEntry } from './furnitureCatalog.js';
import { isFloorCellBlocking, type FloorLayerData } from './floorCatalog.js';

export const DESK_AREA_ZONE_TYPE = 'desk-area';

// 3×3 par défaut (2026-10-13) : les bureaux LimeZu font souvent 2×2 à 3×2,
// il faut une rangée de plus pour le fauteuil. Pas d'écart imposé entre deux
// bureaux : l'espace bureaux laisse de toute façon un passage.
export const DEFAULT_DESK_SIZE = { width: 3, height: 3 } as const;
export const MIN_DESK_SIZE = { width: 2, height: 2 } as const;
export const MAX_DESK_SIZE = { width: 5, height: 4 } as const;
export const MAX_DESK_ITEMS = 12;
/** Calques autorisés pour un objet de bureau (pas de `furnitureTop` : réservé aux admins). */
export const DESK_ITEM_LAYERS = ['furniture', 'floor'] as const;
export type DeskItemLayer = typeof DESK_ITEM_LAYERS[number];

/**
 * Sélection par défaut (tant que l'admin n'a rien réglé), choisie le
 * 2026-10-27 : mobilier du style Copy House (`ch_cc-*`, `ch_mb-*`) et objets
 * dessinés pour Placio (`pl_*`). Avant, tout le catalogue des catégories
 * bureau (LimeZu compris, plusieurs centaines d'objets de styles différents).
 */
export const DEFAULT_DESK_SELECTION: readonly string[] = [
  // Bureaux et tables
  'ch_cc-bureau-reglable', 'ch_cc-bureau-angle', 'ch_mb-bureau', 'ch_mb-bureau-noyer', 'ch_mb-bureau-blanc',
  'pl_bureau_chene', 'pl_bureau_noyer', 'pl_bureau_blanc', 'pl_bureau_noir',
  'pl_poste_chene_ecrans', 'pl_poste_chene_dos', 'pl_poste_chene_droite', 'pl_poste_chene_gauche',
  'pl_poste_blanc_ecrans', 'pl_poste_blanc_dos', 'pl_poste_blanc_droite', 'pl_poste_blanc_gauche',
  'pl_poste_gamer_ecrans', 'pl_poste_gamer_dos',
  'pl_table_basse_noyer', 'pl_table_basse_noyer_profondeur', 'pl_table_basse_chene_garnie',
  // Assises
  'ch_cc-fauteuil-ergonomique-face', 'ch_cc-fauteuil-cocon-face',
  'ch_cc-chaise-face', 'ch_cc-chaise-dos', 'ch_cc-chaise-droite', 'ch_cc-chaise-gauche',
  'ch_mb-fauteuil-face', 'ch_mb-fauteuil-dos', 'ch_mb-fauteuil-droite', 'ch_mb-fauteuil-gauche',
  'pl_fauteuil_bureau_face', 'pl_fauteuil_bureau_dos', 'pl_fauteuil_bureau_droite', 'pl_fauteuil_bureau_gauche',
  'pl_fauteuil_gamer_bleu_face', 'pl_fauteuil_gamer_bleu_dos', 'pl_fauteuil_gamer_bleu_droite', 'pl_fauteuil_gamer_bleu_gauche',
  'pl_fauteuil_gamer_rouge_face', 'pl_fauteuil_gamer_rouge_dos', 'pl_fauteuil_gamer_rouge_droite', 'pl_fauteuil_gamer_rouge_gauche',
  'pl_fauteuil_gamer_violet_face', 'pl_fauteuil_gamer_violet_dos', 'pl_fauteuil_gamer_violet_droite', 'pl_fauteuil_gamer_violet_gauche',
  'ch_cc-fauteuil-club-face', 'ch_cc-fauteuil-club-dos', 'ch_cc-fauteuil-club-droite', 'ch_cc-fauteuil-club-gauche',
  'ch_cc-pouf-face', 'ch_cc-pouf-dos', 'ch_cc-pouf-droite', 'ch_cc-pouf-gauche',
  'ch_cc-canape-deux-face', 'ch_cc-canape-deux-dos',
  // Informatique
  'pl_ecran', 'pl_ecran_dos', 'pl_ecran_profil_droite', 'pl_ecran_profil_gauche', 'pl_double_ecran', 'pl_double_ecran_dos',
  'pl_ecran_incurve', 'pl_ecran_incurve_dos', 'pl_ecran_gamer', 'pl_ecran_gamer_dos',
  'pl_portable', 'pl_clavier_souris', 'pl_clavier_rgb', 'pl_telephone', 'pl_enceinte', 'ch_cc-chariot-visio',
  // Rangement
  'ch_cc-casiers', 'ch_cc-bibliotheque', 'ch_cc-console', 'ch_mb-caisson', 'pl_caisson',
  // Plantes et lumière
  'ch_cc-plante', 'ch_cc-cloison-vegetale', 'pl_plante_bureau', 'ch_cc-lampe-bureau', 'pl_lampe_bureau', 'ch_cc-lampadaire',
  // Tableaux blancs (s'ouvrent dans Excalidraw, privés au titulaire du bureau)
  'pl_paperboard', 'pl_paperboard_notes', 'ch_cc-tableau-blanc', 'ch_mb-tableau-blanc',
  // Objets et tapis
  'pl_tasse', 'pl_pot_crayons', 'pl_livres', 'pl_corbeille_courrier',
  'pl_tapis_gris', 'pl_tapis_berbere', 'pl_tapis_raye', 'pl_tapis_marine', 'pl_tapis_terracotta', 'pl_tapis_sauge',
];
/** Plafond du catalogue réglé par l'admin. */
export const MAX_DESK_CATALOG_SIZE = 500;

export interface Rect { x: number; y: number; width: number; height: number }

const TILE = 32;
const clampInt = (v: unknown, min: number, max: number, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) ? Math.min(max, Math.max(min, v)) : fallback;

/** Taille des bureaux d'un espace, depuis `map_zones.properties` (bornée, 3×2 par défaut). */
export function deskSizeOf(areaProperties: unknown): { width: number; height: number } {
  const p = (areaProperties ?? {}) as Record<string, unknown>;
  return {
    width: clampInt(p['deskWidth'], MIN_DESK_SIZE.width, MAX_DESK_SIZE.width, DEFAULT_DESK_SIZE.width),
    height: clampInt(p['deskHeight'], MIN_DESK_SIZE.height, MAX_DESK_SIZE.height, DEFAULT_DESK_SIZE.height),
  };
}

/** Emprise d'un objet en cases (arrondie, 1 minimum) — même calcul que ZoneScene. */
export function furnitureFootprint(entry: Pick<FurnitureCatalogEntry, 'dimensions'>): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round(entry.dimensions.w / TILE)),
    height: Math.max(1, Math.round(entry.dimensions.h / TILE)),
  };
}

/** Le bureau (relatif à l'espace) tient-il entièrement dans l'espace ? */
export function deskFitsArea(desk: Rect, area: { width: number; height: number }): boolean {
  return Number.isInteger(desk.x) && Number.isInteger(desk.y)
    && desk.x >= 0 && desk.y >= 0
    && desk.x + desk.width <= area.width
    && desk.y + desk.height <= area.height;
}

/** Deux bureaux se chevauchent-ils ? (Collés côte à côte : permis.) */
export function desksOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width
    && a.y < b.y + b.height && b.y < a.y + a.height;
}

/** Tapis, paillassons : posés au sol, sous les avatars, jamais bloquants. */
export function isDeskRug(entry: Pick<FurnitureCatalogEntry, 'id' | 'label'>): boolean {
  return /tapis|paillasson|rug|carpet/i.test(`${entry.label} ${entry.id}`);
}

/** Calque d'un objet de bureau (décidé par l'objet, pas par le membre). */
export function deskItemLayer(entry: FurnitureCatalogEntry): DeskItemLayer {
  return isDeskRug(entry) ? 'floor' : 'furniture';
}

/**
 * Collision d'un objet de bureau : tout bloque sauf les sièges et les tapis
 * (on s'assoit, on marche dessus). Objet de plus d'une rangée : seule la base
 * bloque, l'avatar passe derrière le haut (comme « La base » dans l'éditeur).
 */
export function deskItemCollision(entry: FurnitureCatalogEntry): CollisionGrid | null {
  if (entry.category === 'seating' || isDeskRug(entry)) return null;
  const f = furnitureFootprint(entry);
  return f.height > 1 ? baseOnlyCollisionGrid(f.width, f.height) : ['1'.repeat(f.width)];
}

/**
 * Décalage de profondeur : à rangée du bas égale, un siège passe derrière le
 * reste (fauteuil vu de face posé derrière un bureau). Négligeable face au
 * tri en Y des avatars (100 + y / 10).
 */
export function deskItemDepthOffset(entry: FurnitureCatalogEntry): number {
  return entry.category === 'seating' ? -0.05 : 0;
}

/**
 * Décalages de profondeur de tout un aménagement (même unité que le tri en Y :
 * 100 + bas en px / 10). En plus de la règle des sièges, un objet posé SUR un
 * bureau (écran, lampe, plante…) — il recouvre une table de la catégorie
 * « desks » et son pied n'est pas plus bas que celui de la table — passe
 * au-dessus d'elle, alors que le tri en Y le mettrait derrière (son pied est
 * sur la rangée du haut de la table). Un objet posé devant reste devant.
 */
export function deskItemDepthOffsets(items: ReadonlyArray<{ furnitureId: string; dx: number; dy: number }>): number[] {
  const ROW = TILE / 10; // une rangée en unités de profondeur
  return arrange(items).map((p) => {
    if (!p.entry) return 0;
    if (p.entry.category === 'seating') return deskItemDepthOffset(p.entry);
    return p.onTable ? (p.onTable.bottom - p.bottom) * ROW + 0.05 : 0;
  });
}

/**
 * Collisions de tout un aménagement : deskItemCollision, sauf pour un objet
 * posé sur un bureau (écran, lampe…) — il ne bloque rien, la table bloque
 * déjà sa base, et l'avatar doit pouvoir passer derrière (s'asseoir).
 */
export function deskItemCollisions(items: ReadonlyArray<{ furnitureId: string; dx: number; dy: number }>): Array<CollisionGrid | null> {
  return arrange(items).map((p) => (!p.entry || p.onTable ? null : deskItemCollision(p.entry)));
}

/** Emprise de chaque objet et, s'il est posé sur une table, la table la plus basse qu'il recouvre. */
function arrange(items: ReadonlyArray<{ furnitureId: string; dx: number; dy: number }>) {
  const placed = items.map((i) => {
    const entry = findFurniture(i.furnitureId);
    const f = entry ? furnitureFootprint(entry) : { width: 1, height: 1 };
    return { entry, x0: i.dx, x1: i.dx + f.width, y0: i.dy - f.height + 1, y1: i.dy + 1, bottom: i.dy };
  });
  const tables = placed.filter((p) => p.entry?.category === 'desks');
  return placed.map((p) => {
    let onTable: (typeof placed)[number] | null = null;
    const surface = p.entry && p.entry.category !== 'desks' && p.entry.category !== 'seating' && !isDeskRug(p.entry);
    if (surface) {
      for (const t of tables) {
        const overlaps = p.x0 < t.x1 && t.x0 < p.x1 && p.y0 < t.y1 && t.y0 < p.y1;
        if (overlaps && p.bottom <= t.bottom && (!onTable || t.bottom > onTable.bottom)) onTable = t;
      }
    }
    return { ...p, onTable };
  });
}

export interface DeskItemInput {
  furnitureId: string;
  dx: number;
  dy: number;
  layer?: string;
  isSolid?: boolean;
  collisionGrid?: unknown;
}

/** L'objet (ancré en bas à gauche) tient-il dans le bureau ? */
export function itemFitsDesk(item: { dx: number; dy: number }, footprint: { width: number; height: number }, desk: { width: number; height: number }): boolean {
  return Number.isInteger(item.dx) && Number.isInteger(item.dy)
    && item.dx >= 0 && item.dx + footprint.width <= desk.width
    && item.dy - footprint.height + 1 >= 0 && item.dy < desk.height;
}

/** Objets de la sélection par défaut qui tiennent dans un bureau `maxSize`. */
export function defaultDeskCatalog(maxSize: { width: number; height: number } = MAX_DESK_SIZE): string[] {
  const byId = new Map(furnitureCatalog.filter((e) => !e.hidden).map((e) => [e.id, e]));
  return DEFAULT_DESK_SELECTION.filter((id) => {
    const e = byId.get(id);
    if (!e) return false;
    const f = furnitureFootprint(e);
    return f.width <= maxSize.width && f.height <= maxSize.height;
  });
}

/** Un objet peut-il figurer au catalogue des bureaux (objet visible du catalogue, pas un tampon libre) ? */
export function isDeskCatalogCandidate(id: string): boolean {
  return candidateIds.has(id);
}
// Entrées réelles du catalogue (les objets découpés dans une planche y ont
// aussi un `stamp` ; seuls les tampons libres de l'onglet Planches n'y sont pas).
const candidateIds = new Set(furnitureCatalog.filter((e) => !e.hidden).map((e) => e.id));

/**
 * Catalogue réglé par l'admin, tel qu'enregistré : les objets choisis (`ids`)
 * et la sélection par défaut au moment du réglage (`seen`). Un objet ajouté au
 * catalogue général ensuite n'est pas dans `seen` : il est proposé d'office,
 * l'admin ne l'ayant jamais écarté.
 */
export interface StoredDeskCatalog { ids: string[]; seen: string[] }

export function toStoredDeskCatalog(ids: string[]): StoredDeskCatalog {
  return { ids, seen: defaultDeskCatalog() };
}

/**
 * Objets proposés à partir du réglage enregistré. Ancien format (simple liste,
 * avant le 2026-10-20) : seuls les objets LimeZu existaient quand ces
 * catalogues ont été réglés (`lz_*`) ; nos objets dessinés (`pl_*`) et ceux du
 * monde du campus (`ch_*`) sont arrivés après, et sont donc ajoutés.
 */
export function resolveDeskCatalog(stored: unknown): string[] {
  const strings = (v: unknown) => (Array.isArray(v) ? v.filter((id): id is string => typeof id === 'string') : []);
  const defaults = defaultDeskCatalog();
  let ids: string[];
  let seen: Set<string>;
  if (Array.isArray(stored)) {
    ids = strings(stored);
    seen = new Set(defaults.filter((id) => id.startsWith('lz_')));
  } else {
    const s = (stored ?? {}) as Partial<Record<keyof StoredDeskCatalog, unknown>>;
    ids = strings(s.ids);
    seen = new Set(strings(s.seen));
  }
  // Un objet retiré du catalogue général depuis le réglage disparaît sans bruit.
  const kept = ids.filter(isDeskCatalogCandidate);
  const keptSet = new Set(kept);
  return [...kept, ...defaults.filter((id) => !seen.has(id) && !keptSet.has(id))];
}

export type DeskItemError =
  | 'TOO_MANY_ITEMS'
  | 'FURNITURE_NOT_ALLOWED'
  | 'ITEM_OUT_OF_DESK'
  | 'INVALID_ITEM_LAYER'
  | 'INVALID_COLLISION_GRID';

/**
 * Valide l'aménagement complet d'un bureau. `allowed` = catalogue effectif du
 * tenant. Retourne la première erreur, ou null.
 */
export function validateDeskItems(
  items: DeskItemInput[],
  desk: { width: number; height: number },
  allowed: ReadonlySet<string>,
): DeskItemError | null {
  if (items.length > MAX_DESK_ITEMS) return 'TOO_MANY_ITEMS';
  for (const item of items) {
    const entry = typeof item.furnitureId === 'string' && allowed.has(item.furnitureId) ? findFurniture(item.furnitureId) : undefined;
    if (!entry) return 'FURNITURE_NOT_ALLOWED';
    if (!itemFitsDesk(item, furnitureFootprint(entry), desk)) return 'ITEM_OUT_OF_DESK';
    if (item.layer !== undefined && !(DESK_ITEM_LAYERS as readonly string[]).includes(item.layer)) return 'INVALID_ITEM_LAYER';
    if (item.collisionGrid != null && !isValidCollisionGrid(item.collisionGrid)) return 'INVALID_COLLISION_GRID';
  }
  return null;
}

// ── Obstacles : où un membre ne peut pas poser son bureau ──────────────

/** Cases couvertes par un objet ancré en bas à gauche en (tileX, tileY). */
export function furnitureCells(entry: Pick<FurnitureCatalogEntry, 'dimensions'>, tileX: number, tileY: number): Array<{ x: number; y: number }> {
  const f = furnitureFootprint(entry);
  const cells: Array<{ x: number; y: number }> = [];
  for (let dy = 0; dy < f.height; dy++) {
    for (let dx = 0; dx < f.width; dx++) cells.push({ x: tileX + dx, y: tileY - dy });
  }
  return cells;
}

/**
 * Un objet posé par l'admin empêche-t-il de poser un bureau sur ses cases ?
 * Oui, solide ou non (arbre, déco…), sauf ce qui est « au sol » : tapis,
 * paillassons, ponts — on marche dessus — et ce qui est soi-même un poste
 * (`deskBase` : pupitre du campus, l'emplacement est posé dessus).
 */
export function furnitureBlocksDesk(entry: Pick<FurnitureCatalogEntry, 'walkable' | 'deskBase'>, layer: string): boolean {
  return layer !== 'floor' && !entry.walkable && !entry.deskBase;
}

export interface DeskObstacleSources {
  /** Mobilier de la carte (map_furniture). */
  furniture?: ReadonlyArray<{ furnitureId: string; tileX: number; tileY: number; layer: string }>;
  /** Murs peints dans l'éditeur (map_walls). */
  walls?: ReadonlyArray<{ tileX: number; tileY: number }>;
  /** Portes (zones `door`, en cases), ouvertes ou fermées. */
  doors?: ReadonlyArray<Rect>;
  /** Sols peints (eau bloquante). */
  floor?: FloorLayerData | undefined;
  overlay?: FloorLayerData | undefined;
}

/**
 * Cases interdites à un bureau : objets de l'admin (sauf « au sol »),
 * portes, murs, eau. Partagé par l'API (vérification autoritaire) et le jeu
 * (fantôme rouge) — le jeu y ajoute les collisions du fichier Tiled.
 */
export function deskObstacles(src: DeskObstacleSources): (x: number, y: number) => boolean {
  const cells = new Set<string>();
  const key = (x: number, y: number) => `${x},${y}`;
  for (const item of src.furniture ?? []) {
    const entry = findFurniture(item.furnitureId);
    if (!entry || !furnitureBlocksDesk(entry, item.layer)) continue;
    for (const c of furnitureCells(entry, item.tileX, item.tileY)) cells.add(key(c.x, c.y));
  }
  for (const w of src.walls ?? []) cells.add(key(w.tileX, w.tileY));
  for (const d of src.doors ?? []) {
    for (let y = d.y; y < d.y + d.height; y++) for (let x = d.x; x < d.x + d.width; x++) cells.add(key(x, y));
  }
  return (x, y) => cells.has(key(x, y)) || isFloorCellBlocking(src.floor, src.overlay, x, y);
}

/** Le rectangle (en cases de la carte) touche-t-il un obstacle ? */
export function rectHitsObstacle(rect: Rect, blocked: (x: number, y: number) => boolean): boolean {
  for (let y = rect.y; y < rect.y + rect.height; y++) {
    for (let x = rect.x; x < rect.x + rect.width; x++) if (blocked(x, y)) return true;
  }
  return false;
}

// ── Emplacements de bureaux (2026-10-15, tasks/emplacements-bureaux-plan.md) ──
//
// L'admin ne laisse plus chacun poser son bureau où il veut : l'espace est
// rempli d'emplacements, qu'un membre prend puis aménage.

/** Emplacement, coin haut-gauche relatif à l'espace (en cases). */
export interface DeskSlotPosition { x: number; y: number }

/**
 * Remplit un espace bureaux d'emplacements : rangées de bureaux dos à dos
 * (deux rangées collées), puis une allée d'une case — chaque bureau touche
 * une allée (ou le bord de l'espace). Un emplacement n'est jamais posé sur un
 * obstacle (`blocked`, en cases de la CARTE : mêmes règles qu'un bureau, voir
 * deskObstacles). Tous les décalages de la grille sont essayés ; on garde
 * celui qui place le plus d'emplacements (à égalité, le premier : résultat
 * stable pour un même espace).
 */
export function generateDeskSlots(
  area: Rect,
  size: { width: number; height: number },
  blocked: (x: number, y: number) => boolean = () => false,
): DeskSlotPosition[] {
  const { width: w, height: h } = size;
  if (w > area.width || h > area.height) return [];
  const period = 2 * h + 1;

  // Case libre de l'espace (relative) — précalculée une fois.
  const free = new Uint8Array(area.width * area.height);
  for (let y = 0; y < area.height; y++) {
    for (let x = 0; x < area.width; x++) free[y * area.width + x] = blocked(area.x + x, area.y + y) ? 0 : 1;
  }
  const rectFree = (rx: number, ry: number, taken: Uint8Array): boolean => {
    for (let y = ry; y < ry + h; y++) {
      for (let x = rx; x < rx + w; x++) {
        const i = y * area.width + x;
        if (!free[i] || taken[i]) return false;
      }
    }
    return true;
  };

  let best: DeskSlotPosition[] = [];
  for (let oy = 0; oy < period; oy++) {
    for (let ox = 0; ox < w; ox++) {
      const taken = new Uint8Array(area.width * area.height);
      const slots: DeskSlotPosition[] = [];
      for (let ry = oy; ry + h <= area.height; ry++) {
        const k = (ry - oy) % period;
        if (k !== 0 && k !== h) continue; // seules les deux rangées de chaque bande
        for (let rx = ox; rx + w <= area.width; rx++) {
          if (!rectFree(rx, ry, taken)) continue;
          for (let y = ry; y < ry + h; y++) for (let x = rx; x < rx + w; x++) taken[y * area.width + x] = 1;
          slots.push({ x: rx, y: ry });
        }
      }
      if (slots.length > best.length) best = slots;
    }
  }
  return best;
}

/**
 * Replace des bureaux existants sur des emplacements : chacun, dans l'ordre
 * donné (le plus ancien d'abord), prend l'emplacement libre dont le centre est
 * le plus proche du centre de son ancienne position. Renvoie l'index de
 * l'emplacement de chaque bureau, ou null s'il n'en reste plus.
 */
export function assignDesksToSlots(
  desks: DeskSlotPosition[],
  oldSize: { width: number; height: number },
  slots: DeskSlotPosition[],
  newSize: { width: number; height: number },
): Array<number | null> {
  const used = new Set<number>();
  return desks.map((d) => {
    const cx = d.x + oldSize.width / 2, cy = d.y + oldSize.height / 2;
    let bestIndex: number | null = null;
    let bestDist = Infinity;
    slots.forEach((s, i) => {
      if (used.has(i)) return;
      const dist = (s.x + newSize.width / 2 - cx) ** 2 + (s.y + newSize.height / 2 - cy) ** 2;
      if (dist < bestDist) { bestDist = dist; bestIndex = i; }
    });
    if (bestIndex !== null) used.add(bestIndex);
    return bestIndex;
  });
}

/**
 * Objets d'un bureau qui tiennent encore dans une nouvelle taille (même
 * place relative) ; les autres sont retirés quand l'espace change de taille.
 * Un objet hors catalogue (inconnu) est gardé : il est déjà signalé au membre.
 */
export function deskItemsFitting<T extends { furnitureId: string; dx: number; dy: number }>(
  items: T[],
  size: { width: number; height: number },
): { keep: T[]; drop: T[] } {
  const keep: T[] = [], drop: T[] = [];
  for (const item of items) {
    const entry = findFurniture(item.furnitureId);
    if (!entry || itemFitsDesk(item, furnitureFootprint(entry), size)) keep.push(item);
    else drop.push(item);
  }
  return { keep, drop };
}
